/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon, CheckIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { logo, numberOfProductsWithPlusSign } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import type { SubscriptionEligibleForUpgrade } from 'bundles/coursera-plus/utils/s12nToCourseraPlusUpgradeUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/coursera-plus';

type PropsToComponent = {
  isCourseraPlusAnnualUpgrade: boolean;
  isOpen: boolean;
  onClose: () => void;
  openUpgradeModal: () => void;
  subscription: SubscriptionEligibleForUpgrade;
};

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const useStyles = () => {
  return {
    topLogo: css`
      margin-bottom: 16px;
      padding-left: 48px;
      ${breakpoints.down('sm')} {
        margin-top: 2px;
      }
      ${breakpoints.down('xs')} {
        padding-left: 16px;
      }
    `,
    valueProps: css`
      padding: 0;
      margin: 0;
      list-style-type: none;

      li:first-child {
        margin-top: 0;
      }
    `,
    valuePropListItem: css`
      display: flex;
      align-items: flex-start;
      margin: var(--cds-spacing-150) 0;
    `,
    checkMark: css`
      flex-shrink: 0;
      margin-top: 2px;
      margin-right: var(--cds-spacing-100);
    `,
    actions: css`
      padding-top: 32px;
    `,
  };
};

const S12nToCourseraPlusStartModal = ({
  isCourseraPlusAnnualUpgrade,
  isOpen,
  onClose,
  openUpgradeModal,
  subscription,
}: PropsToComponent) => {
  const { Content, HeadingGroup, Actions } = Dialog;
  const styles = useStyles();

  const handleCloseModal = () => {
    onClose();
  };

  const valueProps = [
    _t('Access {numberOfProducts} courses in AI, business, technology, and more.'),
    _t('Get certificates for every course you finish and boost your chances of getting hired.'),
    _t('Take your skills to the next level with expert-led courses and Coursera Coach, your AI-powered guide.'),
  ];

  return (
    <Dialog
      aria-describedby="dialog-content"
      open={isOpen}
      variant="standard"
      width="medium"
      onClose={handleCloseModal}
      data-testid="s12n_to_coursera_plus_start_modal"
    >
      <Imgix src={logo.PLUS_FULL_BLUE} alt={_t('Coursera Plus logo')} maxWidth={200} css={styles.topLogo} />
      <HeadingGroup>
        {isCourseraPlusAnnualUpgrade ? _t('Upgrade to Coursera Plus annual') : _t('Upgrade to Coursera Plus monthly')}
      </HeadingGroup>
      <Content>
        <TrackedDiv
          withVisibilityTracking={true}
          trackingName="s12n_to_coursera_plus_start_modal"
          trackingData={{ subscriptionId: subscription.id }}
        >
          <ul css={styles.valueProps}>
            {valueProps.map((valueProp) => (
              <li key={valueProp} css={styles.valuePropListItem}>
                <CheckIcon size="medium" color="interactive" css={styles.checkMark} />
                <Typography2 component="p">
                  <FormattedMessage message={valueProp} numberOfProducts={numberOfProductsWithPlusSign} />
                </Typography2>
              </li>
            ))}
          </ul>
        </TrackedDiv>
      </Content>
      <Actions css={styles.actions}>
        <TrackedButton
          variant="primary"
          onClick={openUpgradeModal}
          icon={<ArrowNextIcon size="medium" />}
          data-testid="s12n-to-coursera-plus-upgrade-continue-button"
          trackingName="s12n_to_coursera_plus_upgrade_continue_button"
        >
          {_t('Continue')}
        </TrackedButton>
      </Actions>
    </Dialog>
  );
};

export default S12nToCourseraPlusStartModal;
